/* ============================================================
   Regulatory instruments — date-aware.

   Audit finding: the scaffold hardcoded status as a string and wrote
   every instrument in the future tense. As at 2 Aug 2026 it still asked
   whether operators were "ready" for the KRA ACD mandate (in force the
   next day), the NTSA CVRL (in force a month), and KPA Tariff 2025 (in
   force eight months). Status is now COMPUTED from effectiveDate, so
   the product cannot go stale in that particular way again.

   `verification` is a separate axis from lifecycle status:
     verified          — traced to a gazette notice, legal notice or Act
     authority-notice  — published by the authority itself under a power
                         in its governing Act; operative, but not a
                         statutory instrument
     unconfirmed       — reported by a credible secondary source
     announced         — stated by the authority, instrument not yet
                         published

   `authority-notice` was added on 7 August 2026 because three records —
   the KRA ACD, KPA Tariff 2025 and iCMS — each claimed a "Kenya Gazette
   Notice" that does not exist. The ACD is a KRA public notice, the
   tariff is published by the KPA Board under s.30 of its own Act, and
   iCMS is an administrative migration. All three are real, dated,
   primary publications and all three are enforced; none is a statutory
   instrument. `verified` overclaimed and `unconfirmed` underclaimed, so
   neither was available without saying something untrue.

   check-data.mjs enforces that a `verified` instrument cites a gazette
   or legal notice, and fails the build on an instrument still marked
   `announced` past its own effective date.

   `source` names the publication. `sourceRef` is the LOCATOR — the
   Legal Notice or Gazette Notice number and its date — and it is the
   field that makes a citation checkable by someone who does not take
   our word for it.

   The distinction was found by an external review and it was a fair
   hit. The check demanded the words "gazette" or "legal notice" and
   nothing more, so "Legal Notice — NTSA (Commercial Vehicle Road
   Licence) Regulations" satisfied it while naming no notice a reader
   could go and find. Twelve instruments passed a citation check that
   was testing for a word.

   All twelve now carry a locator. Researching them turned up five
   errors that no date arithmetic and no citation check could have
   found, because in each case the dates were internally consistent and
   the citation named a real publication:

     kebs-pvoc     cited the 2005 programme notice, replaced three times
                   since. The order in force is Legal Notice No. 78 of
                   28 April 2020.
     kpa-tariff    said 1 December 2025. Litigation moved it to
                   22 December 2025.
     eac-ntb-act   said 1 September 2017, seven weeks before the Act was
                   gazetted.
     kra-icms      said 1 July 2019; go-live was the 7th.
     idf / rdl     named the amending Finance and Tax Laws Acts as
                   governing. Both levies are imposed by the
                   Miscellaneous Fees and Levies Act, 2016, ss.7 and 8 —
                   a reader sent to the Finance Act would not find the
                   levy in it.

   The lesson is the same one the NTSA annulment taught. A register can
   be wrong in ways that are invisible from inside it; the only check
   that finds them is going and reading the instrument.
   ============================================================ */

/* Rendered on the instrument. `verification` was stored on all twelve
   records from the beginning and displayed on none of them, which made
   it a provenance claim the reader was never shown and could not
   dispute. The distinction between an Act and a press release is the
   reader's to weigh. */
export const VERIFICATION_META = {
  verified: {
    label: 'Statutory instrument',
    note: null
  },
  'authority-notice': {
    label: 'Authority notice',
    note:
      'Published by the authority under a power in its governing Act. Operative and ' +
      'enforced, but not a statutory instrument — there is no legal notice to look up.'
  },
  unconfirmed: {
    label: 'Unconfirmed',
    note: 'Reported by a credible secondary source. Confirm before relying on it.'
  },
  announced: {
    label: 'Announced',
    note: 'Stated by the authority; the instrument has not been published.'
  }
};

export const REGULATIONS = [
  {
    id: 'kra-acd',
    name: 'Advance Cargo Declaration (ACD)',
    authority: 'KRA',
    governingAct: 'East African Community Customs Management Act, 2004',
    effectiveDate: '2026-08-03',
    /* Not a Gazette Notice, and this record used to say it was. KRA
       announced the ACD in a public notice on 14 July 2026 and brought
       it into operation on 3 August. That is a real, dated, primary
       publication by the authority — but it is not a statutory
       instrument, and calling it one lends it an authority no reader
       could verify. */
    verification: 'authority-notice',
    source: 'KRA Customs & Border Control public notice — Advance Cargo Declaration platform',
    sourceRef: 'KRA public notice of 14 July 2026; platform operative from 3 August 2026',
    summary:
      'Applies to containerised sea cargo destined for Kenya, both FCL and LCL. A 15-digit ' +
      'ACD reference must appear on the Bill of Lading before the shipment is ' +
      'loaded at origin. Shipments loaded without a valid reference face penalties and ' +
      'clearance delays on arrival.',
    appliesTo: ['importer', 'clearing-agent'],
    cost: 'No direct fee; non-compliance penalties apply',
    renewal: null,
    portal: 'https://acd.kra.go.ke',
    action:
      'Confirm every supplier and origin agent obtains the ACD reference before loading, and ' +
      'that your Bill of Lading template carries the field.'
  },
  {
    id: 'ntsa-cvrl',
    name: 'Commercial Vehicle Road Licence (CVRL)',
    authority: 'NTSA',
    governingAct: 'National Transport and Safety Authority Act, 2012',
    effectiveDate: '2026-07-01',
    verification: 'verified',
    source:
      'The National Transport and Safety (Operation of Commercial Vehicles) Regulations, 2026',
    sourceRef: 'Legal Notice No. 14 of 2026, gazetted 13 February 2026',
    revoked: {
      when: 'July 2026',
      by: 'Senate resolution under section 18 of the Statutory Instruments Act',
      note:
        'The Senate adopted the report of its Select Committee on Delegated Legislation and ' +
        'annulled Legal Notice No. 14 of 2026, together with Legal Notice No. 13 of 2026 ' +
        '(Traffic (Motor Vehicle Inspection) Rules). The licence and the equipment mandates ' +
        'it introduced cannot be enforced.',
      fallBack:
        'The predecessor instrument, Legal Notice No. 3 of 2018 (published 2 February 2018), ' +
        'was not annulled and provides for a commercial vehicle licence valid for one year ' +
        'at the fee in its Second Schedule. Confirm the current fee and the licence you ' +
        'actually need with NTSA before paying anything.'
    },
    summary:
      'Introduced a per-vehicle road licence alongside an operator licence, commencing ' +
      '1 July 2026. Annulled before it could be enforced.',
    appliesTo: ['transporter'],
    cost: 'No longer payable under this instrument',
    renewal: 'annual',
    action:
      'Do not pay or renew against Legal Notice No. 14. If you already paid or fitted ' +
      'equipment for the 1 July 2026 commencement, take it up with NTSA. Your surviving ' +
      'obligations sit under Legal Notice No. 3 of 2018 — confirm them directly.'
  },
  {
    id: 'ntsa-cvol',
    name: 'Commercial Vehicle Operator Licence (CVOL)',
    authority: 'NTSA',
    governingAct: 'National Transport and Safety Authority Act, 2012',
    effectiveDate: '2026-07-01',
    verification: 'verified',
    source:
      'The National Transport and Safety (Operation of Commercial Vehicles) Regulations, 2026',
    sourceRef: 'Legal Notice No. 14 of 2026, gazetted 13 February 2026',
    revoked: {
      when: 'July 2026',
      by: 'Senate resolution under section 18 of the Statutory Instruments Act',
      note:
        'Annulled with the rest of Legal Notice No. 14 of 2026. The operator licence, the ' +
        'telematics and underride-protection mandates and the prescribed driving-hours ' +
        'regime introduced for 1 July 2026 cannot be enforced. NTSA had already suspended ' +
        'enforcement of the telematics requirement before the annulment.',
      fallBack:
        'Legal Notice No. 3 of 2018 was not annulled and remains the operative instrument ' +
        'for commercial vehicle operation. Confirm your position under it with NTSA.'
    },
    summary:
      'Would have required an operator licence, a nominated safety officer, telematics, ' +
      'underride protection and prescribed driving hours from 1 July 2026. Annulled.',
    appliesTo: ['transporter'],
    cost: 'No longer payable under this instrument',
    renewal: 'annual',
    action:
      'Do not act on the 1 July 2026 requirements. Nothing here obliges you to fit ' +
      'telematics or appoint a safety officer under Legal Notice No. 14. Check what Legal ' +
      'Notice No. 3 of 2018 requires of you instead.'
  },
  {
    id: 'kpa-tariff-2025',
    name: 'KPA Tariff 2025',
    authority: 'KPA',
    governingAct: 'Kenya Ports Authority Act, Cap 391',
    /* Was 1 December 2025, which was never a date this tariff had. It
       was published in August to commence 15 September, the Container
       Freight Station Association challenged it, and it took effect on
       22 December once judgment was delivered in Mombasa HCJR/E023/2025.
       Charges apply to vessels arriving and orders lodged after 0001h on
       that day. Three weeks is not a rounding error on a fee schedule
       that replaced rates standing since 2012. */
    effectiveDate: '2025-12-22',
    /* Prepared and published by the KPA Board under section 30 of its
       own Act, with the Cabinet Secretary approving major alterations.
       That is the tariff's actual provenance; it is not a Gazette
       Notice, which is what this record used to claim. */
    verification: 'authority-notice',
    source: 'Kenya Ports Authority Tariff 2025, published by the KPA Board',
    sourceRef:
      'Kenya Ports Authority Act, Cap 391, s.30; effective 22 December 2025 following ' +
      'judgment in Mombasa HCJR/E023/2025',
    summary:
      'Revised schedule of port handling, storage and ancillary charges at Mombasa and Lamu ' +
      'and the inland container depots, replacing the 2012 tariff.',
    appliesTo: ['importer', 'clearing-agent', 'transporter'],
    cost: 'Varies by service and container size',
    renewal: null,
    action:
      'Rebase landed-cost models and re-paper client contracts that quote pre-2025 rates. ' +
      'The tariff bites on arrival and lodgement date, so shipments straddling 22 December ' +
      '2025 may be charged on either side of it.'
  },
  {
    id: 'rdl-2pc',
    name: 'Railway Development Levy at 2%',
    authority: 'KRA',
    /* The governing Act is the one that imposes the levy, not the one
       that last moved the rate. RDL lives in section 8 of the
       Miscellaneous Fees and Levies Act; the Tax Laws (Amendment) Act,
       2024 amended that section. Naming the amending Act as governing
       sends a reader to a statute that will not contain the levy. */
    governingAct: 'Miscellaneous Fees and Levies Act, 2016 (Cap. 469C), s.8',
    effectiveDate: '2024-12-27',
    verification: 'verified',
    source: 'Miscellaneous Fees and Levies Act, 2016 (No. 29 of 2016, Cap. 469C), s.8',
    sourceRef:
      'Rate raised to 2% by the Tax Laws (Amendment) Act, 2024 (No. 12 of 2024), ' +
      'assented 11 December 2024, effective 27 December 2024',
    summary: 'RDL charged at 2% of CIF value on imports, raised from 1.5%.',
    appliesTo: ['importer'],
    cost: '2% of CIF',
    renewal: null,
    action: 'Confirm the 2% rate is in your duty calculator and quoted landed costs.'
  },
  {
    id: 'idf-2-5pc',
    name: 'Import Declaration Fee at 2.5%',
    authority: 'KRA',
    /* Same correction as RDL: IDF is imposed by section 7 of the
       Miscellaneous Fees and Levies Act. The Finance Act, 2023 set the
       current rate. */
    governingAct: 'Miscellaneous Fees and Levies Act, 2016 (Cap. 469C), s.7',
    effectiveDate: '2023-07-01',
    verification: 'verified',
    source: 'Miscellaneous Fees and Levies Act, 2016 (No. 29 of 2016, Cap. 469C), s.7',
    sourceRef:
      'Rate set at 2.5% by the Finance Act, 2023 (No. 4 of 2023), Kenya Gazette Supplement ' +
      'No. 97 (Acts No. 4), assented 26 June 2023, effective 1 July 2023',
    summary:
      'IDF charged at 2.5% of CIF value, reduced from 3.5%. The Finance Act, 2023 also ' +
      'removed the preferential 1.5% rate for raw materials and intermediate goods, so the ' +
      '2.5% rate is now uniform.',
    appliesTo: ['importer'],
    cost: '2.5% of CIF',
    renewal: null,
    action: 'File the IDF before the goods leave origin to avoid arrival-side delay.'
  },
  {
    id: 'kebs-pvoc',
    name: 'Pre-Export Verification of Conformity (PVoC)',
    authority: 'KEBS',
    governingAct: 'Standards Act, Cap 496',
    /* Was 29 September 2005 — the date the PVoC programme began under
       Legal Notice No. 78 of 15 July 2005. That instrument has been
       replaced three times since: by Legal Notice No. 127 of 2018, then
       Legal Notice No. 183 of 2019, then the order in force today. An
       importer who followed the old citation would have been reading a
       revoked instrument to find out what inspection they needed.

       The obligation is live; it was the citation that was twenty-one
       years stale. That is the quieter failure mode behind the NTSA
       annulment: the register was as capable of pointing at a repealed
       instrument as at an annulled one, and neither is caught by a
       date comparison. */
    effectiveDate: '2020-04-28',
    verification: 'verified',
    source:
      'The Standards (Verification of Conformity to Standards and Other Applicable ' +
      'Regulations) Order, 2020',
    sourceRef:
      'Legal Notice No. 78 of 28 April 2020, which revoked Legal Notice No. 183 of 2019',
    summary:
      'Regulated goods must be inspected at origin by a KEBS-appointed agency and carry a ' +
      'Certificate of Conformity before shipment. The current order dates from 2020; the ' +
      'programme itself has run since 2005 and the list of appointed agencies and exempted ' +
      'products is revised separately from the order.',
    appliesTo: ['importer'],
    cost: 'Charged by the appointed inspection agency, typically % of FOB',
    renewal: 'per shipment',
    action:
      'Brief suppliers at order stage, not at booking. Destination inspection is materially ' +
      'more expensive and slower.'
  },
  {
    id: 'kra-customs-agent-licence',
    name: 'Customs Agent Licence',
    authority: 'KRA',
    governingAct: 'East African Community Customs Management Act, 2004',
    effectiveDate: '2005-01-01',
    verification: 'verified',
    source: 'East African Community Customs Management Act, 2004, s.145',
    sourceRef:
      'EAC Gazette No. 1, Vol. AT 1, 1 January 2005 (EAC Act Supplement No. 1); ' +
      'assented 31 December 2004',
    summary:
      'Only KRA-licensed agents may lodge customs declarations. Licences are renewed annually.',
    appliesTo: ['clearing-agent'],
    cost: 'Annual licence fee plus security bond',
    renewal: 'annual',
    action: 'Verify your agent’s licence number against the KRA register each year.'
  },
  {
    id: 'kra-bonded-warehouse',
    name: 'Bonded Warehouse Licence and CB6 Bond',
    authority: 'KRA',
    governingAct: 'East African Community Customs Management Act, 2004',
    effectiveDate: '2005-01-01',
    verification: 'verified',
    source: 'East African Community Customs Management Act, 2004, Part IX',
    sourceRef:
      'EAC Gazette No. 1, Vol. AT 1, 1 January 2005 (EAC Act Supplement No. 1); ' +
      'assented 31 December 2004',
    summary:
      'Bonded warehouse operators require a current licence and an active CB6 security bond ' +
      'guaranteeing duty on goods held.',
    appliesTo: ['warehouse'],
    cost: 'Licence fee plus bond premium',
    renewal: 'annual',
    action: 'Reconcile bond value against peak duty exposure, not average stock.'
  },
  {
    id: 'eac-cma-2004',
    name: 'EAC Customs Management Act, 2004',
    authority: 'EAC',
    governingAct: 'East African Community Customs Management Act, 2004',
    effectiveDate: '2005-01-01',
    verification: 'verified',
    source: 'East African Community Customs Management Act, 2004',
    sourceRef:
      'EAC Gazette No. 1, Vol. AT 1, 1 January 2005 (EAC Act Supplement No. 1); ' +
      'assented 31 December 2004, commenced 1 January 2005',
    summary:
      'The primary customs statute across EAC partner states, governing declarations, ' +
      'transit, warehousing and enforcement.',
    appliesTo: ['importer', 'clearing-agent', 'transporter', 'warehouse'],
    cost: null,
    renewal: null,
    action: 'Baseline reference for all cross-border procedure within the EAC.'
  },
  {
    id: 'eac-ntb-act',
    name: 'EAC Elimination of Non-Tariff Barriers Act, 2017',
    authority: 'EAC',
    governingAct: 'EAC Elimination of Non-Tariff Barriers Act, 2017',
    /* Was 1 September 2017, a date nothing supports. What is on the
       record is the gazettal: EAC Gazette No. 12 of 27 October 2017. An
       Act cannot be in force before it is published, so the old date
       was not merely unsourced, it was impossible. The commencement
       date proper could not be established from here — the gazettal
       date is used because it is the earliest date the Act could have
       operated and the only one that can be pointed at. */
    effectiveDate: '2017-10-27',
    verification: 'verified',
    source: 'East African Community Elimination of Non-Tariff Barriers Act, 2017',
    sourceRef: 'EAC Gazette No. 12 of 27 October 2017',
    summary:
      'Establishes the legal mechanism for reporting and resolving non-tariff barriers ' +
      'between partner states, with defined resolution timelines.',
    appliesTo: ['transporter', 'clearing-agent', 'importer'],
    cost: null,
    renewal: null,
    action:
      'Barriers can be reported through the AfCFTA online mechanism at tradebarriers.africa. ' +
      'Unreported barriers are never resolved.'
  },
  {
    id: 'kra-icms',
    name: 'Integrated Customs Management System (iCMS)',
    authority: 'KRA',
    governingAct: 'East African Community Customs Management Act, 2004',
    /* Was 1 July 2019, an approximation. Go-live for imports was 7 July
       2019, applying to cargo whose expected date of arrival fell on or
       after that day. */
    effectiveDate: '2019-07-07',
    /* An administrative migration announced by the Commissioner of
       Customs and Border Control. There is no instrument here at all,
       which is exactly why claiming a Gazette Notice was the wrong
       thing to do. */
    verification: 'authority-notice',
    source:
      'KRA Commissioner of Customs and Border Control public notice — implementation of ' +
      'the Integrated Customs Management System for cargo clearance',
    sourceRef:
      'KRA public notice of 14 June 2019; go-live for imports 7 July 2019 at Mombasa, ' +
      'ICD Nairobi and the border stations',
    summary:
      'KRA’s electronic platform for declaration, risk profiling, assessment, release and ' +
      'post-clearance audit, integrated with KenTrade and other agency systems.',
    appliesTo: ['clearing-agent', 'importer'],
    cost: null,
    renewal: null,
    action: 'Ensure agents lodge digitally; paper-adjacent workflows now create the delay.'
  }
];

/* ============================================================
   Status engine
   ============================================================ */

export const IMMINENT_WINDOW_DAYS = 90;

/* When this register was last read against source, and how long that
   can be true for.

   Every instrument states where it comes from. None of them stated when
   anyone last LOOKED, and for an offline-first product that is the gap
   that matters: the app is built to keep working with no network, so a
   visitor can be computing duty and levy figures weeks after they last
   connected, with nothing on screen distinguishing a register read this
   morning from one read last winter.

   Today is the argument. Two records cited a Legal Notice the Senate
   had annulled, five more carried dates or governing Acts that were
   wrong, and every one of them rendered with total confidence. The
   register cannot promise it is right. It can promise to say how old it
   is, which is the thing a reader can actually act on — and it is the
   difference between a figure and a figure you know to re-check.

   Bumped by whoever re-reads the instruments, not by the build. A date
   moved by CI would measure deployments and be mistaken for research. */
export const REGISTER_REVIEWED = '2026-08-07';

/* Past this the register is not wrong, it is unvouched-for. 90 days
   because Kenyan instruments move on gazette timescales, not annual
   ones: three of the twelve records here changed in the last eight
   months, and one of those was annulled six weeks after commencing. */
export const REGISTER_MAX_AGE_DAYS = 90;

/**
 * How stale the register is, in whole days.
 * @param {Date} [now]
 * @returns {number}
 */
export function registerAgeDays(now = new Date()) {
  const reviewed = new Date(REGISTER_REVIEWED);
  return Math.floor((now - reviewed) / 86400000);
}

/**
 * What to tell the reader about the register's age. Always something —
 * a fresh register saying so is what makes a stale one legible.
 * @param {Date} [now]
 * @returns {{days: number, stale: boolean, label: string}}
 */
export function registerFreshness(now = new Date()) {
  const days = registerAgeDays(now);
  const stale = days > REGISTER_MAX_AGE_DAYS;
  if (days <= 0) return { days, stale, label: 'Instruments last read against source today' };
  if (days === 1) return { days, stale, label: 'Instruments last read against source yesterday' };
  return {
    days,
    stale,
    label: `Instruments last read against source ${days} days ago`
  };
}

/**
 * Lifecycle status computed from the effective date.
 * @returns {'in-force'|'imminent'|'announced'|'superseded'}
 */
export function statusOf(regulation, now = new Date()) {
  /* Checked before anything date-derived, because an annulled
     instrument's commencement date is exactly the thing that would
     otherwise make it read "In force".

     `revoked` is deliberately not `superseded`. Superseded means
     replaced — the obligation survives in a newer instrument and the
     reader needs to go and find it. Revoked means struck down with
     nothing in its place: the obligation does not exist. Collapsing the
     two would tell an operator to go looking for a replacement that was
     never made. */
  if (regulation.revoked) return 'revoked';
  if (regulation.supersededBy) return 'superseded';

  const effective = new Date(regulation.effectiveDate);
  if (Number.isNaN(effective.getTime())) return 'announced';

  const days = Math.round(
    (Date.UTC(effective.getUTCFullYear(), effective.getUTCMonth(), effective.getUTCDate()) -
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) /
      86400000
  );

  if (days <= 0) return 'in-force';
  if (days <= IMMINENT_WINDOW_DAYS) return 'imminent';
  return 'announced';
}

export const STATUS_META = {
  'in-force': { label: 'In force', tone: 'positive' },
  imminent: { label: 'Imminent', tone: 'warning' },
  announced: { label: 'Announced', tone: 'info' },
  superseded: { label: 'Superseded', tone: 'muted' },
  /* Danger, not muted. A superseded instrument is background; an
     annulled one is a live correction to something the reader may have
     already acted on — bought telematics, appointed a safety officer,
     paid a fee. It should be the loudest badge on the page. */
  revoked: { label: 'Annulled', tone: 'danger' }
};

export function getRegulation(id) {
  return REGULATIONS.find((r) => r.id === id) || null;
}

export function regulationsFor(profile, now = new Date()) {
  return REGULATIONS.filter((r) => !profile || r.appliesTo.includes(profile)).map((r) => ({
    ...r,
    status: statusOf(r, now)
  }));
}

/** Everything in force or landing within the window, soonest first. */
export function upcomingObligations(now = new Date()) {
  return REGULATIONS.map((r) => ({ ...r, status: statusOf(r, now) }))
    .filter((r) => r.status === 'imminent' || r.status === 'announced')
    .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
}

/** Instruments carrying a recurring renewal — the calendar's spine. */
export function renewableObligations() {
  return REGULATIONS.filter((r) => r.renewal === 'annual');
}
